/**
 * useMapViewPersistence Hook
 *
 * Provides map viewport persistence with hybrid localStorage + Firestore storage.
 * - localStorage: Fast reads on page load (no network latency)
 * - Firestore: Cross-device sync (following existing user_info pattern)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/authContext';
import {
  loadViewportFromLocalStorage,
  loadViewportFromFirestore,
  saveViewportToLocalStorage,
  saveViewportToFirestore,
  debounce,
  throttle,
  DEFAULT_VIEWPORT
} from '../utils/mapViewPersistence';

// Debounce delay for localStorage saves (ms)
const LOCAL_SAVE_DEBOUNCE = 1500;

// Throttle delay for Firestore saves (ms)
const FIRESTORE_SAVE_THROTTLE = 30000;

/**
 * Hook for persisting and restoring map viewport state
 *
 * @returns {Object} containing:
 *   - initialViewState: The viewport to initialize the map with
 *   - isLoading: Whether we're still loading the saved viewport
 *   - saveViewport: Function to call when viewport changes
 */
export function useMapViewPersistence() {
  const { currentUser, userLoggedIn } = useAuth();
  const [initialViewState, setInitialViewState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Keep track of latest viewport for beforeunload
  const latestViewportRef = useRef(null);

  // Create debounced/throttled save functions
  const debouncedLocalSave = useCallback(
    debounce((viewport) => {
      saveViewportToLocalStorage(viewport);
    }, LOCAL_SAVE_DEBOUNCE),
    []
  );

  const throttledFirestoreSave = useCallback(
    throttle((viewport) => {
      if (userLoggedIn) {
        saveViewportToFirestore(viewport);
      }
    }, FIRESTORE_SAVE_THROTTLE),
    [userLoggedIn]
  );

  // Load saved viewport on mount
  useEffect(() => {
    async function loadSavedViewport() {
      setIsLoading(true);

      // First, try localStorage (fast)
      const localViewport = loadViewportFromLocalStorage();

      if (localViewport) {
        // Use localStorage data immediately
        setInitialViewState({
          latitude: localViewport.latitude,
          longitude: localViewport.longitude,
          zoom: localViewport.zoom
        });
      }

      // If user is logged in, check Firestore for potentially newer data
      if (userLoggedIn && currentUser) {
        try {
          const firestoreViewport = await loadViewportFromFirestore();

          if (firestoreViewport) {
            // Use Firestore data if it's newer than localStorage
            const localTime = localViewport?.lastUpdated || 0;
            const firestoreTime = firestoreViewport.lastUpdated || 0;

            if (firestoreTime > localTime) {
              setInitialViewState({
                latitude: firestoreViewport.latitude,
                longitude: firestoreViewport.longitude,
                zoom: firestoreViewport.zoom
              });
              // Sync localStorage with Firestore data
              saveViewportToLocalStorage(firestoreViewport);
            }
          } else if (!localViewport) {
            // No saved data anywhere, use defaults
            setInitialViewState(DEFAULT_VIEWPORT);
          }
        } catch (error) {
          console.warn('Error loading viewport from Firestore:', error);
          // If Firestore fails and we don't have local data, use defaults
          if (!localViewport) {
            setInitialViewState(DEFAULT_VIEWPORT);
          }
        }
      } else if (!localViewport) {
        // Not logged in and no local data, use defaults
        setInitialViewState(DEFAULT_VIEWPORT);
      }

      setIsLoading(false);
    }

    loadSavedViewport();
  }, [userLoggedIn, currentUser]);

  // Save viewport function to be called on map move
  const saveViewport = useCallback((latitude, longitude, zoom) => {
    const viewport = { latitude, longitude, zoom };
    latestViewportRef.current = viewport;

    // Debounced save to localStorage
    debouncedLocalSave(viewport);

    // Throttled save to Firestore
    throttledFirestoreSave(viewport);
  }, [debouncedLocalSave, throttledFirestoreSave]);

  // Handle beforeunload - save immediately
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (latestViewportRef.current) {
        // Synchronous save to localStorage before page unloads
        saveViewportToLocalStorage(latestViewportRef.current);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  return {
    initialViewState: initialViewState || DEFAULT_VIEWPORT,
    isLoading,
    saveViewport
  };
}
